import { cn } from '@anytag/shared-utils';
import { useUserRoles } from '@anytag/auth';
import { UNEXPECTED_ERROR } from '@anytag/shared-constants';
import { useQueryHelper, useToggleState } from '@anytag/shared-hooks';
import { Button, ButtonProps, BaseModal, ModalFooter, ModalBody, Form } from '@anytag/shared-atoms';
import {
  TikTokSpecialCampaignDetail,
  TikTokSpecialCampaignStatus,
  useRejectTiktokSpecialCampaignMutation
} from '@anytag/graphql';
import { TextareaField } from '@anytag/shared-molecules';
import { FormAction } from '@anytag/shared-types';

interface RejectFormValues {
  reason: string;
}

interface RejectButtonProps extends ButtonProps {
  tiktokSpecialCampaign?: TikTokSpecialCampaignDetail;
}

export const RejectButton = ({ tiktokSpecialCampaign, ...buttonProps }: RejectButtonProps) => {
  const confirmModal = useToggleState();
  const { isAdminStaff } = useUserRoles();
  const { t, enqueueSnackbar, invalidateRouteLoader } = useQueryHelper();
  const { callRejectTiktokSpecialCampaign } = useRejectTiktokSpecialCampaignMutation({
    onCompleted: invalidateRouteLoader
  });

  // TODO: we might need to check permissions here as well
  // Asking: https://anymindgroup.slack.com/archives/C02C5J5B7KL/p1730428908925939
  const hasRejectButton =
    isAdminStaff && tiktokSpecialCampaign && tiktokSpecialCampaign?.status === TikTokSpecialCampaignStatus.REVIEWING;

  const handleReject: FormAction<RejectFormValues>['onSubmit'] = async ({ reason }) => {
    if (!hasRejectButton) return;

    try {
      await callRejectTiktokSpecialCampaign({ variables: { input: { campaignId: tiktokSpecialCampaign.id, reason } } });

      enqueueSnackbar(t('succeededInUpdating', { name: tiktokSpecialCampaign.title }), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(t(error?.message || UNEXPECTED_ERROR), { variant: 'error' });
    } finally {
      confirmModal.close();
    }
  };

  return (
    hasRejectButton && (
      <>
        <Button
          color="danger"
          variant="solid"
          title={t('Button.Reject')}
          onClick={confirmModal.open}
          {...buttonProps}
          className={cn('min-w-25', buttonProps.className)}
        />

        <BaseModal
          open={confirmModal.status}
          onClose={confirmModal.close}
          description={t('rejectMkpCampaignReasonDescription')}
          title={t('Are you sure you want to reject the campaign?')}
          asChild
        >
          <Form.Provider<RejectFormValues> onSubmit={handleReject}>
            {({ formState }) => (
              <>
                <ModalBody>
                  <TextareaField name="reason" title={t('Reason of rejection')} optional />
                </ModalBody>

                <ModalFooter
                  cancelButton={{ onClick: confirmModal.close }}
                  submitButton={{
                    type: 'submit',
                    color: 'danger',
                    loading: { showIcon: true, status: formState.isSubmitting },
                    title: t(formState.isSubmitting ? 'Button.Rejecting' : 'Button.Reject')
                  }}
                />
              </>
            )}
          </Form.Provider>
        </BaseModal>
      </>
    )
  );
};
